import React, { useEffect, useState } from "react";
import {
  Form,
  InputNumber,
  Button,
  message,
  Input,
  Popconfirm,
} from "antd";
import inputStyles from "../../../../styles/Custom/Input.module.css";
import { printBill } from "../../../../components/Receptionist/receptionKeys";
import btnStyles from "../../../../styles/Custom/Button.module.css";
import {
  addPaymentApi,
  shareBillApi,
} from "../../../../services/receptionist.services";
import PaymentModeSelector from "./PaymentModeSelector";
import styles from "./BillingIp.module.css";
import generateIcon from "../../../../assets/receptionistIcons/ipBilling/generate.svg";
import {
  createCashAmountRule,
  syncCashAmountValidation,
  validateCashPaymentAmount,
} from "../../../../helpers/paymentValidation";

const PaymentModeField = ({ value, onChange, form }) => (
  <PaymentModeSelector
    value={value}
    onChange={(mode) => {
      onChange?.(mode);
      syncCashAmountValidation(form, mode, "amount");
    }}
  />
);

const AdvancePayment = ({ billData, refreshData, activePatient }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const paymentMode = Form.useWatch("mode", form);

  useEffect(() => {
    syncCashAmountValidation(form, paymentMode, "amount");
  }, [paymentMode, form]);


  useEffect(() => {
    if (activePatient) return;
    const due = Number(
      billData?.data?.payment_details?.total_due_amount || 0
    );
    if (due <= 0) return;
    if (form.isFieldTouched("amount")) return;
    if (form.getFieldValue("amount")) return;
    form.setFieldsValue({ amount: Number(due.toFixed(2)) });
  }, [activePatient, billData, form]);

  const onFinish = (values) => {
    const cashError = validateCashPaymentAmount(values?.amount, values?.mode);
    if (cashError) {
      message.error(cashError);
      return;
    }

    setLoading(true);
    const payload = {
      ...values,
    };

    addPaymentApi(billData?.data?.bill?.id, payload)
      .then((res) => {
        message.success(res.data.message);
        form.resetFields();
        refreshData();
        printBill(res?.data?.data?.bill, res?.data?.data?.id, true);
        setLoading(false);
      })
      .catch((err) => {
        message.error(err.response?.data?.message);
        setLoading(false);
      });
  };

  const downLoadBill = (id) => {
    shareBillApi(id, `type=bill&share=download`)
      .then((res) => {
        const s3Link = res?.data?.data;
        if (s3Link) {
          window.open(s3Link, "_blank");
        } else {
          message.error("Failed to retrieve download link.");
        }
      })
      .catch(() => message.error("Error downloading the bill."));
  };

  const shareBill = (id) => {
    shareBillApi(id, `type=bill&share=email`)
      .then(() => message.success("Bill Shared Successfully"))
      .catch((err) => message.error(err?.response?.data?.message));
  };

  return (
    <Form layout="vertical" onFinish={onFinish} form={form}>
      <label className={styles.formLabel} htmlFor="payment-amount">
        Amount
      </label>
      <Form.Item
        name="amount"
        className={styles.amountInput}
        rules={[
          { required: true, message: "Please enter amount" },
          createCashAmountRule(form, "mode"),
        ]}
      >
        <InputNumber
          id="payment-amount"
          addonBefore="₹"
          controls={false}
          min={0}
          placeholder="0.00"
          className={`${inputStyles.mdNumberInput} ${styles.settlementAmountInput}`}
        />
      </Form.Item>

      <label className={styles.formLabel} style={{ marginTop: 16 }}>
        Payment mode
      </label>
      <Form.Item
        name="mode"
        rules={[{ required: true, message: "Please select a payment mode" }]}
      >
        <PaymentModeField form={form} />
      </Form.Item>

      {paymentMode && paymentMode !== "cash" && (
        <>
          <label className={styles.formLabel} style={{ marginTop: 16 }}>
            Transaction number
          </label>
          <Form.Item name="transaction_number">
            <Input
              className={inputStyles.mdInput}
              placeholder="Enter transaction ID"
            />
          </Form.Item>
        </>
      )}

      <label className={styles.formLabel} style={{ marginTop: 16 }}>
        Remarks
      </label>
      <Form.Item name="remarks" className={styles.remarksInput}>
        <Input.TextArea placeholder="Optional note for receipt" rows={3} />
      </Form.Item>

      <div className={styles.settlementActions}>
        <Popconfirm
          title="Payment confirmation"
          description="Have you verified the payment details?"
          okText="Yes"
          cancelText="No"
          onConfirm={() => form.submit()}
        >
          <Button
            loading={loading}
            type="primary"
            className={`${btnStyles.lgBtn} ${styles.submitBtn}`}
            icon={
              <img src={generateIcon} alt="" className={styles.submitBtnIcon} />
            }
          >
            Generate receipt
          </Button>
        </Popconfirm>
      </div>

      {!activePatient && (
        <div className={styles.dischargedBillActions}>
          <div className={styles.dischargedBillRow}>
            <Button
              loading={loading}
              type="primary"
              className={`${btnStyles.mdBtn} ${styles.dischargedBillBtn}`}
              onClick={() => downLoadBill(billData?.data?.bill?.id)}
            >
              Download Bill
            </Button>
            <Button
              loading={loading}
              type="primary"
              ghost
              className={`${btnStyles.mdBtn} ${styles.dischargedBillBtn}`}
              onClick={() => shareBill(billData?.data?.bill?.id)}
            >
              Share Bill
            </Button>
          </div>
        </div>
      )}
    </Form>
  );
};

export default AdvancePayment;
